import React, { useEffect, useRef, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  useFieldArray,
  useWatch
} from 'react-hook-form'

import { Icon, Switch, Textarea } from '@clientbase/clientbase-library'
import {
  Box,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useInsertAtCursor } from 'hooks/useInsertAtCursor'
import useTags from 'hooks/useTags'
import { Products } from 'models/Plans'
import { ChargeRecurrenceBodyModalForm } from 'templates/Charge/interface'

import { useSessionCookies } from 'components/CompanySelector'

import { priceConverter } from 'utils/FormatMoney'
import {
  deriveRecurrenceFrequency,
  FREQUENCY_NAME_TO_ENUM,
  FrequencyUnit,
  ItemCadence,
  pairToPreset,
  presetToPair
} from 'utils/frequency'
import { prepareFormDataWithTags } from 'utils/tags'

import { theme } from 'styles/theme'

import PaymentsType, { PaymentsTypeRef } from '../../components/PaymentsType'
import AdvancedOptions from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/AdvancedOptions'
import Composition from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/Composition'
import { RecurrenceCycle } from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/Composition/Composition.interface'
import CyclesAccordion from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/CyclesAccordion'
import ExpirationSettings from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/ExpirationSettings'
import Frequency from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/Frequency'
import OptionsAdditionals from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/OptionsAdditionals'
import { useConfigurePlan } from '../../templates/Customers/Modals/BodyModalNewChargeOrRecurrence/hooks/useConfigurePlan'
import TypesCharges from '../../templates/v2/Products/Form/components/TypesCharges/TypesCharges'
import ChargeVariablePicker from '../ChargeVariablePicker'
import { deriveLinkTotalCycles, CycleWindowItem } from './deriveLinkTotalCycles'

/**
 * Seções disponíveis no SharedChargeFields que podem ser ocultadas
 *
 * @example
 * // Para ocultar apenas a seção de pagamentos e opções avançadas:
 * <SharedChargeFields hiddenSections={['paymentsType', 'advancedOptions']} />
 *
 * // Para mostrar apenas composição e frequência:
 * <SharedChargeFields hiddenSections={['expirationSettings', 'cyclesAccordion', 'paymentsType', 'description', 'optionsAdditionals', 'advancedOptions']} />
 */
export type SharedChargeFieldsSection =
  | 'composition'
  | 'frequency'
  | 'dueDate'
  | 'expirationSettings'
  | 'cyclesAccordion'
  | 'paymentsType'
  | 'description'
  | 'optionsAdditionals'
  | 'advancedOptions'
  | 'itemsOnDescription'

export interface SharedChargeFieldsProps {
  control: Control<ChargeRecurrenceBodyModalForm>
  watch: UseFormWatch<ChargeRecurrenceBodyModalForm>
  setValue: UseFormSetValue<ChargeRecurrenceBodyModalForm>
  trigger: UseFormTrigger<ChargeRecurrenceBodyModalForm>
  errors: FieldErrors<ChargeRecurrenceBodyModalForm>
  register: any
  clearErrors: UseFormClearErrors<ChargeRecurrenceBodyModalForm>
  isCharge: boolean
  setIsCharge: React.Dispatch<React.SetStateAction<boolean>>
  isUnique: boolean
  setIsUnique: React.Dispatch<React.SetStateAction<boolean>>
  isEdit?: boolean
  isDuplicate?: boolean
  editData?: any
  customerUuid?: string
  planDefault?: Products
  editType?: 'charge' | 'recurrence'
  hiddenSections?: SharedChargeFieldsSection[]
  isProductOrLink?: boolean
  externalReplaceDiscounts?: (discounts: any[]) => void
  paymentsTypeRef?: React.RefObject<PaymentsTypeRef>
  onScrollToCreditCardReady?: (scrollFn: () => void) => void
  contractModels?: { label: string; value: string }[]
  contractValidation?: {
    loading: boolean
    blocked: boolean
    blockReason: string
    missingCustomFields: Array<{ uuid: string; title: string }>
  }
  onContractModelChange?: (uuid: string | null) => void
  onAddContractCustomField?: (field: { uuid: string; title: string }) => void
  onAddAllContractCustomFields?: () => void
}

const SharedChargeFields: React.FC<SharedChargeFieldsProps> = ({
  control,
  watch,
  setValue,
  trigger,
  errors,
  register,
  clearErrors,
  isCharge,
  setIsCharge,
  isUnique,
  setIsUnique,
  isEdit = false,
  isDuplicate = false,
  editData,
  customerUuid,
  planDefault,
  editType = 'charge',
  hiddenSections = [],
  isProductOrLink = false,
  externalReplaceDiscounts,
  paymentsTypeRef,
  onScrollToCreditCardReady,
  contractModels = [],
  contractValidation,
  onContractModelChange,
  onAddContractCustomField,
  onAddAllContractCustomFields
}) => {
  // SC-10999: na visão multi-empresas o prazo padrão é o da empresa escolhida
  const { ['nextauth.limit_days_for_payment']: limitDaysForPayment } =
    useSessionCookies()
  const {
    fields: itemsFields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'items'
  })

  const { tags: availableTags } = useTags()

  const [totalValue, setTotalValue] = useState<number>(0)
  // A prévia é calculada pelo CyclesAccordion; guardada aqui para o botão que
  // abre o modal, que fica no cabeçalho da composição.
  const [previewCycles, setPreviewCycles] = useState<RecurrenceCycle[]>([])
  const [hasAlreadyCalledPlanToValues, setHasAlreadyCalledPlanValues] =
    useState(false)
  const [storedCycleValue, setStoredCycleValue] = useState(!isUnique ? 0 : 1)
  const [disableRecurrentTab, setDisableRecurrentTab] = useState(false)
  const [disableUniqueTab, setDisableUniqueTab] = useState(false)
  const [userManuallySelectedCycles, setUserManuallySelectedCycles] =
    useState(false)
  const [userEditingCustomCycles, setUserEditingCustomCycles] = useState(false)
  // Quando o merchant escolhe a frequência à mão, ela deixa de ser derivada. Sem isso a
  // derivação roda no render seguinte e reverte a escolha — a mensagem de incompatibilidade
  // aparecia e sumia no mesmo instante.
  const [frequencyManuallySet, setFrequencyManuallySet] = useState(false)

  const [recurrenceHasCCRecurring, setRecurrenceHasCCRecurring] =
    useState(false)

  useEffect(() => {
    if (!isEdit || !isUnique) return
    const rec: any = (editData as any)?.recurrence
    if (!rec) return

    const has =
      Boolean(rec?.credit_card_recurring) ||
      (typeof rec?.payment_type === 'string' &&
        rec.payment_type.includes('credit_card_recurring'))
    setRecurrenceHasCCRecurring(has)
  }, [isEdit, isUnique, editData?.recurrence])

  // Quando editar uma cobrança única originada de recorrência com cartão recorrente,
  // inicializar o switch de recorrência no cartão e garantir o card ligado
  useEffect(() => {
    if (!isEdit || !isUnique) return
    if (!editData?.recurrenceUuid) return
    if (!recurrenceHasCCRecurring) return

    const ccEnabled = watch('payment_type_details.credit_card.enabled')
    const ccRecurring = watch(
      'payment_type_details.credit_card.credit_card_recurring.enabled'
    )
    const currentPayments = (watch('payments') as string[]) || []

    if (!ccEnabled) {
      setValue('payment_type_details.credit_card.enabled', true)
    }
    if (!ccRecurring) {
      setValue(
        'payment_type_details.credit_card.credit_card_recurring.enabled',
        true
      )
    }
    if (!currentPayments.includes('credit_card')) {
      setValue('payments', [...currentPayments, 'credit_card'])
    }
  }, [
    isEdit,
    isUnique,
    editData?.recurrenceUuid,
    recurrenceHasCCRecurring,
    setValue,
    watch
  ])

  const internalDiscountsFieldArray = useFieldArray({
    control,
    name: 'discounts'
  })

  const replaceDiscountsToUse =
    externalReplaceDiscounts || internalDiscountsFieldArray.replace

  const { configureSelectedPlan } = useConfigurePlan({
    control,
    replaceDiscounts: replaceDiscountsToUse
  })

  const { description, billingDescription, payments, plan } = watch()

  const { trackCursor, insertAtCursor } = useInsertAtCursor()

  // Para produtos, usar billingDescription; para cobranças/recorrências, usar description
  const getCurrentDescription = () =>
    isProductOrLink ? billingDescription : description

  // Helper function para verificar se uma seção deve ser renderizada
  const shouldRenderSection = (section: SharedChargeFieldsSection): boolean => {
    return !hiddenSections.includes(section)
  }

  const itemsWatch = watch(
    itemsFields
      .map((_, index) => [
        `items.${index}.price` as const,
        `items.${index}.quantity` as const,
        `items.${index}.description` as const,
        `items.${index}.product.value` as const
      ])
      .flat()
  )

  const clearedErrorsRef = useRef<Record<string, boolean>>({})

  // Reset cleared errors when items change
  useEffect(() => {
    clearedErrorsRef.current = {}
  }, [itemsFields.length])

  useEffect(() => {
    const total = itemsFields.reduce((sum, _, index) => {
      const price = priceConverter(watch(`items.${index}.price`) || 0)
      const quantity = Number(watch(`items.${index}.quantity`)) || 0
      const product = price * quantity

      const description = watch(`items.${index}.description`)
      const productValue = watch(`items.${index}.product.value`)

      if (
        (description || productValue) &&
        !clearedErrorsRef.current[`items.${index}`]
      ) {
        clearErrors(`items.${index}.description`)
        clearErrors(`items.${index}.product`)
        clearErrors(`items.${index}`)
        clearedErrorsRef.current[`items.${index}`] = true

        // Trigger validation to update isValid state after clearing errors
        setTimeout(() => {
          trigger(`items.${index}`)
        }, 0)
      }

      const currentTotal = watch(`items.${index}.total`)
      if (currentTotal !== product) {
        setValue(`items.${index}.total`, product, { shouldValidate: false })
      }

      return sum + product
    }, 0)

    setTotalValue(total)
  }, [
    itemsWatch,
    itemsFields,
    watch,
    clearErrors,
    setValue,
    setTotalValue,
    trigger
  ])

  const prevLength = useRef(itemsFields.length)

  useEffect(() => {
    const setItemValue = async () => {
      if (
        itemsFields.length == 1 &&
        !!watch(`items.0.product`) &&
        !!watch(`items.0.product`)?.value &&
        !hasAlreadyCalledPlanToValues &&
        !isEdit && // Only configure plan in creation mode, not in edit mode
        !isDuplicate // Não configura plano em modo duplicação — já veio pronto da recorrência
      ) {
        setHasAlreadyCalledPlanValues(true)

        configureSelectedPlan({
          plan: watch(`items.0.product`)?.value,
          setValue,
          planDefault,
          item: true,
          setIsUnique,
          isEdit,
          setIsCharge,
          currentDescription: isProductOrLink
            ? watch('billingDescription')
            : watch('description'),
          isProductOrLink
        })
      }

      prevLength.current = itemsFields.length
    }

    setItemValue()
  }, [itemsWatch, isEdit])

  /**
   * Frequência da recorrência derivada dos planos escolhidos: o de menor período dita, e os
   * demais precisam ser múltiplos inteiros dele. Assim o plano mais frequente cobra em todo
   * ciclo e nenhum ciclo fica vazio.
   *
   * Em efeito PRÓPRIO de propósito. Isto morava junto do cálculo de total de ciclos, atrás
   * de guards que não têm nada a ver com frequência (produto sem ciclos válidos, item
   * personalizado, "primeira seleção") — bastava um deles para o campo nunca ser preenchido.
   *
   * Não deriva na edição: o backend trava a frequência depois que existe cobrança paga.
   */
  useEffect(() => {
    if (isEdit) return
    // Escolha manual vence a derivação. Item incompatível com ela não é corrigido em
    // silêncio: vira aviso no formulário e bloqueio no submit.
    if (frequencyManuallySet) return

    const planos = itemsFields
      .map((_, index) => watch(`items.${index}.product`) as any)
      .filter((p) => p?.value && p.value !== 'custom' && p.frequency)
      .map((p) => ({
        cadencia: {
          key: p.value,
          label: p.label || '',
          pair: presetToPair(
            p.frequency,
            p.frequency_type as FrequencyUnit | undefined,
            Number(p.frequency_number) || undefined
          )
        } as ItemCadence,
        // Plano de cobrança única não impõe cadência, mas se for o único da composição
        // a frequência precisa sair de algum lugar — vira o palpite inicial, editável.
        cobrancaUnica: p.total_cycles === 1
      }))

    // Composição só com itens avulsos (descrição livre, sem plano): não há de onde
    // derivar, mas a recorrência precisa de uma cadência. Cai no padrão mensal em
    // vez de deixar o select em branco — o merchant troca se quiser.
    let unit: FrequencyUnit
    let count: number

    if (!planos.length) {
      // Só semeia o vazio: se o merchant já escolheu algo, respeita.
      if (watch('frequency') !== undefined && watch('frequency') !== null)
        return
      ;[unit, count] = presetToPair('monthly')
    } else {
      const ditam = planos.filter((p) => !p.cobrancaUnica)
      const derivada = deriveRecurrenceFrequency(
        (ditam.length ? ditam : planos).map((p) => p.cadencia)
      )

      if (!derivada.pair || derivada.incompatible.length > 0) return
      ;[unit, count] = derivada.pair
    }

    const enumValue =
      FREQUENCY_NAME_TO_ENUM[pairToPreset(unit, count) ?? 'custom'] ?? 7

    if (watch('frequency') !== enumValue) setValue('frequency', enumValue)
    if (watch('frequency_type') !== unit) setValue('frequency_type', unit)
    if (watch('frequency_number') !== count) setValue('frequency_number', count)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsWatch, isEdit, frequencyManuallySet])

  useEffect(() => {
    // CORREÇÃO: Only configure plan based on first item in creation mode, not in edit mode
    // E apenas se não tiver chamado configurePlan antes (para evitar sobrescrever configurações existentes)
    if (
      itemsFields.length === 1 &&
      !isEdit &&
      !isDuplicate &&
      !hasAlreadyCalledPlanToValues
    ) {
      configureSelectedPlan({
        plan: watch(`items.0.product`)?.value,
        setValue,
        planDefault,
        item: true,
        setIsUnique,
        isEdit,
        setIsCharge,
        currentDescription: isProductOrLink
          ? watch('billingDescription')
          : watch('description'),
        isProductOrLink
      })
    }
  }, [watch(`items.0.product`)?.value, isEdit, hasAlreadyCalledPlanToValues])

  // Efeito para atualizar o total de ciclos e frequência quando um produto for adicionado
  // Referência para armazenar o timeout de debounce
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Referência para armazenar os valores anteriores
  const prevValuesRef = useRef({
    totalCycles: watch('totalCycles'),
    totalCyclesCustom: watch('totalCyclesCustom')
  })

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      if (!itemsWatch || itemsWatch.length === 0) return
      if (isEdit) return
      const newTotalValue = itemsFields.reduce((sum, _, index) => {
        const price = priceConverter(watch(`items.${index}.price`) || 0)
        const quantity = Number(watch(`items.${index}.quantity`)) || 0
        return sum + price * quantity
      }, 0)
      setTotalValue(newTotalValue)

      // Verifica se há produtos selecionados antes de fazer qualquer coisa
      const hasSelectedProducts = itemsFields.some(
        (_, index) => watch(`items.${index}.product`)?.value
      )

      if (!hasSelectedProducts) {
        setDisableRecurrentTab(false)
        setDisableUniqueTab(false)
        return
      }

      // Verifica se há itens personalizados (não produtos)
      const hasCustomItems = itemsFields.some(
        (_, index) =>
          watch(`items.${index}.description`) &&
          !watch(`items.${index}.product`)?.value
      )

      const selectedProducts = itemsFields
        .map((_, index) => {
          const product = watch(`items.${index}.product`)

          return product && product.value && product.value !== 'custom'
            ? {
                uuid: product.value,
                label: product.label,
                totalCycles: product.total_cycles ?? 1,
                frequency: (product as any).frequency,
                frequency_type: (product as any).frequency_type,
                frequency_number: (product as any).frequency_number
              }
            : null
        })
        .filter(Boolean)

      const hasProductsWithValidCycles = selectedProducts.every(
        (product) => product && product.totalCycles !== undefined
      )

      if (selectedProducts.length > 0 && !hasProductsWithValidCycles) {
        return
      }

      // Verifica se há algum produto recorrente (totalCycles > 1 ou totalCycles = 0)
      const hasRecurrentProduct = selectedProducts.some(
        (product) =>
          product && (product.totalCycles > 1 || product.totalCycles === 0)
      )

      const hasMixedProducts =
        selectedProducts.length > 0 &&
        !hasCustomItems &&
        hasRecurrentProduct &&
        selectedProducts.some((product) => product && product.totalCycles === 1)
      const firstProduct = selectedProducts[0]
      const isReallyRecurrent =
        firstProduct &&
        firstProduct.totalCycles !== 1 &&
        watch(`items.0.product`)?.total_cycles !== undefined

      if (
        isUnique &&
        hasRecurrentProduct &&
        !hasCustomItems &&
        selectedProducts.length === 1 &&
        isReallyRecurrent &&
        !hasMixedProducts
      ) {
        setIsUnique(false)
        setIsCharge(false)

        // Quando converter para recorrente, atualizar os ciclos baseado no produto recorrente
        // Encontrar o primeiro produto recorrente para definir os ciclos
        const firstRecurrentProduct = selectedProducts.find(
          (product) =>
            product && (product.totalCycles > 1 || product.totalCycles === 0)
        )

        if (firstRecurrentProduct) {
          if (firstRecurrentProduct.totalCycles === 0) {
            // Produto com ciclos infinitos
            setValue('totalCycles', 0)
          } else if (firstRecurrentProduct.totalCycles > 12) {
            // Produto com mais de 12 ciclos - usar "Outros"
            setValue('totalCycles', 13)
            setValue(
              'totalCyclesCustom',
              String(firstRecurrentProduct.totalCycles)
            )
            setStoredCycleValue(13)
          } else {
            // Produto com ciclos normais (2-12)
            setValue('totalCycles', firstRecurrentProduct.totalCycles)
            setStoredCycleValue(firstRecurrentProduct.totalCycles)
          }
        }
      }

      const hasOnlyNonRecurrentProducts =
        selectedProducts.length > 0 && !hasCustomItems && !hasRecurrentProduct

      const hasOnlyRecurrentProducts =
        selectedProducts.length > 0 &&
        !hasCustomItems &&
        hasRecurrentProduct &&
        selectedProducts.every(
          (product) =>
            product && (product.totalCycles > 1 || product.totalCycles === 0)
        )

      if (selectedProducts.length === 0 || hasMixedProducts || hasCustomItems) {
        setDisableRecurrentTab(false)
        setDisableUniqueTab(false)
      } else {
        setDisableRecurrentTab(hasOnlyNonRecurrentProducts)
        setDisableUniqueTab(hasOnlyRecurrentProducts)
      }

      const isFirstProductSelection =
        selectedProducts.length === 1 && !hasCustomItems
      if (
        !isUnique &&
        hasOnlyNonRecurrentProducts &&
        isFirstProductSelection &&
        !hasMixedProducts
      ) {
        setIsUnique(true)
        setIsCharge(true)
        setValue('totalCycles', 1)
        setStoredCycleValue(1)
      }

      // Obtém o total de ciclos atual do formulário
      const currentTotalCycles = watch('totalCycles') || 0

      // Só serve ao total de ciclos. A FREQUÊNCIA não sai mais daqui: ela é derivada da
      // cadência dos planos, logo abaixo — "o produto com mais ciclos" nunca foi um critério
      // de frequência, era só o que estava à mão.
      //
      // O teto sai da JANELA DO ITEM, com o teto do plano só como default de quem não
      // configurou janela. Lendo `product.total_cycles` direto, mexer nos ciclos do item não
      // movia este campo e o link era gravado com um teto que a cobrança nunca alcançaria
      // (SC-10950).
      const cycleWindowItems = itemsFields
        .map((_, index) => {
          const product = watch(`items.${index}.product`)
          if (!product || !product.value || product.value === 'custom') {
            return null
          }

          return {
            totalCycles: Number(watch(`items.${index}.total_cycles`) ?? 0),
            startCycle: Number(watch(`items.${index}.start_cycle`) ?? 1),
            cycleInterval: Number(watch(`items.${index}.cycle_interval`) ?? 1),
            productTotalCycles: (product as any).total_cycles ?? 1
          }
        })
        .filter(Boolean) as CycleWindowItem[]

      const maxCycles = deriveLinkTotalCycles(cycleWindowItems)

      // Atualiza o total de ciclos se for diferente do atual e o usuário não tiver selecionado manualmente
      // Importante: não atualiza se o usuário tiver selecionado manualmente (clicado na aba ou alterado o valor)
      // CORREÇÃO: Só atualizar ciclos se for o primeiro produto sendo selecionado
      // ou se não houver configuração manual prévia
      // E NÃO resetar valores quando o usuário está editando ciclos personalizados
      if (
        maxCycles !== currentTotalCycles &&
        !userManuallySelectedCycles &&
        !userEditingCustomCycles &&
        selectedProducts.length === 1 && // Apenas quando há 1 produto selecionado
        !hasCustomItems // E não há itens personalizados
        // O guard `currentTotalCycles !== 13` saiu daqui: ele existia para não sobrescrever
        // um valor personalizado, mas quem já protege isso são os dois flags de intenção
        // acima (a escolha manual e a digitação no campo "Outros"). Com ele, um plano de
        // mais de 12 ciclos jogava o campo no sentinela 13 e travava qualquer derivação
        // posterior — mexer na janela do item nunca mais movia o teto do link (SC-10950).
      ) {
        // Se maxCycles > 12, configurar para "Outros" (13) e definir totalCyclesCustom
        if (maxCycles > 12) {
          setValue('totalCycles', 13)
          setValue('totalCyclesCustom', String(maxCycles))
          setStoredCycleValue(13) // Atualizar storedCycleValue para manter consistência
        } else {
          setValue('totalCycles', maxCycles)
          setStoredCycleValue(maxCycles) // Atualizar storedCycleValue para manter consistência
        }
      }

      // Atualizar valores de referência após processamento
      setTimeout(() => {
        prevValuesRef.current = {
          totalCycles: watch('totalCycles'),
          totalCyclesCustom: watch('totalCyclesCustom')
        }
      }, 200)
    }, 300) // Debounce de 300ms

    // Limpa o timeout quando o componente for desmontado
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [itemsWatch, userManuallySelectedCycles, userEditingCustomCycles])

  const hasItemsOnDescription = useWatch({
    control,
    name: 'hasItemsOnDescription'
  })

  // Helper para verificar se a descrição contém a variável de itens
  const checkIfDescriptionHasItemsVariable = () => {
    const descriptionField = isProductOrLink
      ? 'billingDescription'
      : 'description'
    const currentDescription = watch(descriptionField as any) || ''
    return currentDescription.includes('{{itens_da_cobranca}}')
  }

  // Effect simples: apenas ativa o switch na edição se a descrição já tem a variável
  useEffect(() => {
    if (isEdit) {
      const descriptionField = isProductOrLink
        ? 'billingDescription'
        : 'description'
      const currentDescription = watch(descriptionField as any) || ''
      const hasVariable = currentDescription.includes('{{itens_da_cobranca}}')

      if (hasVariable && !hasItemsOnDescription) {
        setValue('hasItemsOnDescription', true)
      }
    }
  }, [isEdit, isProductOrLink, setValue]) // Só executa uma vez na edição

  // Effect para adicionar/remover a variável baseado no switch (ação do usuário)
  useEffect(() => {
    const descriptionField = isProductOrLink
      ? 'billingDescription'
      : 'description'
    const currentDescription = watch(descriptionField as any) || ''

    if (hasItemsOnDescription) {
      // Se estiver ativado e a variável não existir no texto, adiciona
      if (!currentDescription.includes('{{itens_da_cobranca}}')) {
        const newDescription = currentDescription
          ? currentDescription.concat('\n{{itens_da_cobranca}}')
          : '{{itens_da_cobranca}}'
        setValue(descriptionField as any, newDescription)
      }
    } else {
      // Se não estiver ativado, remove a variável {{itens_da_cobranca}} se existir
      const descriptionWithoutItems = currentDescription
        .replace('{{itens_da_cobranca}}', '')
        .trim()
      setValue(descriptionField as any, descriptionWithoutItems)
    }
  }, [hasItemsOnDescription, isProductOrLink, setValue])

  useEffect(() => {
    // CORREÇÃO: Não chama configurePlan quando estamos no modo de edição
    // E apenas se não tiver chamado configurePlan antes (para evitar sobrescrever configurações existentes)
    // E apenas se plan não for undefined/null (para evitar chamadas desnecessárias)
    if (!isEdit && !isDuplicate && plan && !hasAlreadyCalledPlanToValues) {
      configureSelectedPlan({
        plan,
        setValue,
        planDefault,
        setIsUnique,
        isEdit,
        currentDescription: isProductOrLink
          ? watch('billingDescription')
          : watch('description'),
        isProductOrLink
      })
    }
  }, [plan, isEdit, hasAlreadyCalledPlanToValues])

  const updateDescriptionWithItems = () => {
    // Verifica se o switch "Incluir os itens na descrição da fatura" está ativado
    const descriptionField = isProductOrLink
      ? 'billingDescription'
      : 'description'
    const currentDescription = watch(descriptionField as any) || ''

    if (!watch('hasItemsOnDescription')) {
      // Se não estiver ativado, remove a variável {{itens_da_cobranca}} se existir
      const descriptionWithoutItems = currentDescription
        .replace('{{itens_da_cobranca}}', '')
        .trim()
      setValue(descriptionField as any, descriptionWithoutItems)
      return
    }

    // Se estiver ativado e a variável não existir no texto, adiciona
    if (!currentDescription.includes('{{itens_da_cobranca}}')) {
      const newDescription = currentDescription
        ? currentDescription.concat('\n{{itens_da_cobranca}}')
        : '{{itens_da_cobranca}}'
      setValue(descriptionField as any, newDescription)
    }
  }

  // Effect para desligar bonificação quando mudar para cobrança única
  // IMPORTANTE: Produtos e links são templates e podem ter bonificação mesmo sendo únicos
  useEffect(() => {
    // Não desligar bonificação automaticamente para produtos/links
    if (isProductOrLink) return

    const currentTotalCycles = watch('totalCycles')
    const hasBonus = watch('hasBonus')

    // Para charges/recurrences: Se totalCycles for 1 (cobrança única) e bonificação estiver ativada, desligar
    if (currentTotalCycles === 1 && hasBonus) {
      setValue('hasBonus', false)
    }
  }, [watch('totalCycles'), watch, setValue, isProductOrLink])

  useEffect(() => {
    if (isEdit && editData && availableTags.length > 0) {
      const formDataWithTags = prepareFormDataWithTags(editData, availableTags)

      // Se há tags para converter, atualizar o formulário
      if (formDataWithTags.tags && formDataWithTags.tags.length > 0) {
        setValue('tags', formDataWithTags.tags)
      }
    }
  }, [isEdit, editData, availableTags, setValue])

  // useEffect para processar tags do produto quando configurePlan é chamado
  useEffect(() => {
    if (!isEdit && availableTags.length > 0) {
      const currentTags = watch('tags')

      // Se há tags no formulário que precisam ser processadas
      if (currentTags && Array.isArray(currentTags) && currentTags.length > 0) {
        // Verificar se as tags já estão no formato correto (objetos com id)
        const needsProcessing = currentTags.some(
          (tag) =>
            typeof tag === 'string' ||
            (typeof tag === 'object' && tag !== null && !('id' in tag))
        )

        if (needsProcessing) {
          const formDataWithTags = prepareFormDataWithTags(
            { tags: currentTags },
            availableTags
          )

          if (formDataWithTags.tags && formDataWithTags.tags.length > 0) {
            setValue('tags', formDataWithTags.tags)
          }
        }
      }
    }
  }, [isEdit, availableTags, setValue])

  return (
    <>
      {shouldRenderSection('composition') && (
        <Composition
          previewCycles={previewCycles}
          control={control}
          isCharge={isCharge}
          watch={watch}
          itemsFields={itemsFields}
          appendItem={append as any}
          removeItem={remove}
          totalValue={totalValue}
          setValue={setValue}
          errors={errors}
          updateDescriptionWithItems={updateDescriptionWithItems}
          isEdit={isEdit}
          isPaymentLink={isProductOrLink}
          // "Esta composição vai virar uma recorrência" — é o que habilita a janela de
          // ciclos por item. Vale para a recorrência avulsa e também para o link de
          // pagamento RECORRENTE (isUnique = false), que gera uma Recurrence no checkout
          // e cujos itens já carregam start_cycle/total_cycles no backend
          // (ProductLink#apply_recurrence_cycle_window).
          isRecurrence={!isCharge && (!isProductOrLink || !isUnique)}
          currentCycle={(editData as any)?.currentCycle}
        />
      )}

      {shouldRenderSection('frequency') && (
        <Frequency
          control={control}
          watch={watch}
          setValue={setValue}
          storedCycleValue={storedCycleValue}
          setStoredCycleValue={setStoredCycleValue}
          isUnique={isUnique}
          setIsUnique={setIsUnique}
          setIsCharge={setIsCharge}
          edit={isEdit}
          editRecurrence={
            isEdit && editType === 'recurrence' && !isProductOrLink
          }
          disableRecurrentTab={
            disableRecurrentTab ||
            (isEdit && editType === 'charge' && !isProductOrLink)
          }
          disableUniqueTab={
            disableUniqueTab ||
            (isEdit && editType === 'recurrence' && !isProductOrLink)
          }
          onUserManualSelection={() => setUserManuallySelectedCycles(true)}
          onUserManualFrequency={() => setFrequencyManuallySet(true)}
          onUserEditingCustomCycles={setUserEditingCustomCycles}
          itemsFields={itemsFields}
          setDisableRecurrentTab={setDisableRecurrentTab}
          setDisableUniqueTab={setDisableUniqueTab}
          userManuallySelectedCycles={userManuallySelectedCycles}
          userEditingCustomCycles={userEditingCustomCycles}
          isProductOrLink={isProductOrLink}
          hasPaidBillings={editData?.hasPaidBillings}
        />
      )}

      {shouldRenderSection('dueDate') && (
        <Box mt={!isProductOrLink ? -2 : 0}>
          {isProductOrLink ? (
            <Stack gap={1}>
              <Typography variant="mdBold">Vencimento</Typography>
              <TypesCharges
                control={control}
                watch={watch}
                setValue={setValue}
                // Mesma condição que antes liberava o switch por item (o ItemRow
                // recebe isPaymentLink={isProductOrLink}).
                showProrateItems={isProductOrLink}
              />
            </Stack>
          ) : (
            <ExpirationSettings
              control={control}
              isCharge={isCharge}
              watch={watch}
              setValue={setValue}
              edit={isEdit}
              dueDate={editData?.dueDate}
              dueDay={editData?.dueDay}
              expirationField={editData?.expirationField}
            />
          )}
        </Box>
      )}

      {shouldRenderSection('cyclesAccordion') && (
        <CyclesAccordion
          onCyclesChange={setPreviewCycles}
          isUnique={isUnique}
          watch={watch}
          isEditingCharge={isEdit && isCharge}
          isEditingRecurrence={isEdit && editType === 'recurrence'}
          editData={editData}
        />
      )}

      {shouldRenderSection('paymentsType') && (
        <PaymentsType
          ref={paymentsTypeRef}
          payments={payments}
          register={register}
          totalValue={totalValue}
          control={control}
          setValue={setValue}
          errors={errors}
          customerUuid={customerUuid}
          watch={watch}
          selectCreditCard={true}
          isRecurrence={!isUnique}
          isUnique={isUnique}
          recurrenceUuid={editData?.recurrenceUuid}
          isProductOrLink={isProductOrLink}
          recurrenceHasCreditCardRecurring={
            recurrenceHasCCRecurring ||
            Boolean(
              (editData as any)?.recurrence?.credit_card_recurring ||
                (editData as any)?.recurrence?.payment_type?.includes(
                  'credit_card_recurring'
                )
            )
          }
          onScrollToCreditCardReady={onScrollToCreditCardReady}
        />
      )}

      {shouldRenderSection('description') && (
        <Box mt="18px">
          <Controller
            control={control}
            name={isProductOrLink ? 'billingDescription' : 'description'}
            render={({ field: { onChange, value }, fieldState }) => (
              <div>
                <Textarea
                  fullWidth={true}
                  onChange={onChange}
                  onSelect={trackCursor}
                  placeholder="Descrição"
                  error={!!fieldState.error?.message}
                  helperText={fieldState.error?.message}
                  data-cy="billingDescription"
                  label={
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" gap="5px">
                        <Typography mt={0.5}>Descreva sua cobrança</Typography>
                        <Tooltip
                          arrow
                          enterTouchDelay={0}
                          title="Este campo é destinado para adicionar informações complementares, exemplo: nome de outro responsável, nome do dependente, código de identificação, etc."
                        >
                          <Box position="relative">
                            <Icon
                              icon="info"
                              colorSX={theme.palette.neutral[600]}
                            />
                          </Box>
                        </Tooltip>
                      </Box>

                      <Typography
                        color={theme.palette.neutral[400]}
                        sx={{ fontSize: '12px', fontWeight: 300 }}
                      >
                        Caracteres: {getCurrentDescription()?.length}/2000
                      </Typography>
                    </Stack>
                  }
                  value={value}
                  maxLength={2000}
                  style={{
                    overflow: 'auto',
                    height: '75px',
                    resize: 'vertical',
                    maxHeight: '50vh'
                  }}
                  name="description"
                />
              </div>
            )}
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={
              shouldRenderSection('itemsOnDescription')
                ? 'space-between'
                : 'flex-end'
            }
          >
            {shouldRenderSection('itemsOnDescription') && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Tooltip
                  title={
                    !itemsFields?.length || totalValue === 0
                      ? 'Adicione pelo menos um item com valor para incluir na descrição'
                      : ''
                  }
                  arrow
                >
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={watch('hasItemsOnDescription')}
                          disabled={!itemsFields?.length || totalValue === 0}
                          onChange={(e) => {
                            const newValue = e.target.checked
                            setValue('hasItemsOnDescription', newValue)
                          }}
                        />
                      }
                      label={
                        <Typography
                          fontSize="14px"
                          color={theme.palette.neutral[400]}
                        >
                          Incluir os itens na descrição da fatura
                        </Typography>
                      }
                    />
                  </Box>
                </Tooltip>
              </Stack>
            )}

            <ChargeVariablePicker
              description={getCurrentDescription() || ''}
              setDescription={(message) =>
                setValue(
                  isProductOrLink ? 'billingDescription' : 'description',
                  message
                )
              }
              isRecurrence={!isUnique}
              insertAtCursor={insertAtCursor}
              hasItemsVariable={checkIfDescriptionHasItemsVariable()}
              onAddItemsVariable={() => {
                if (!itemsFields?.length || totalValue === 0) return
                setValue('hasItemsOnDescription', true)
              }}
            />
          </Stack>
        </Box>
      )}

      {shouldRenderSection('optionsAdditionals') && (
        <OptionsAdditionals
          control={control}
          watch={watch}
          setValue={setValue}
          trigger={trigger}
          clearErrors={clearErrors}
          isEditingRecurrence={isEdit && editType === 'recurrence'}
          isProductOrLink={isProductOrLink}
          recurrence={!isCharge}
          isUnique={isUnique}
          totalCycles={watch('totalCycles')}
          externalReplaceDiscounts={externalReplaceDiscounts}
          recurrenceFrequency={watch('frequency')}
          currentCycle={editData?.currentCycle}
          currentCycleDueDate={
            // Find the billing for the current cycle, or use the last one (most recent)
            editData?.billings?.find(
              (b: any) =>
                String(b.recurrenceCycle) === String(editData?.currentCycle)
            )?.dueDate ||
            editData?.billings?.[editData?.billings?.length - 1]?.dueDate
          }
        />
      )}

      {shouldRenderSection('advancedOptions') && (
        <AdvancedOptions
          control={control}
          watch={watch}
          setValue={setValue}
          trigger={trigger}
          clearErrors={clearErrors}
          isCharge={isCharge}
          isChargeOrRecurrenceForm={true}
          limit_days_for_payment={limitDaysForPayment || '60'}
          editData={editData}
          isProductOrLink={isProductOrLink}
          contractModels={contractModels}
          contractValidation={contractValidation}
          onContractModelChange={onContractModelChange}
          onAddContractCustomField={onAddContractCustomField}
          onAddAllContractCustomFields={onAddAllContractCustomFields}
        />
      )}
    </>
  )
}

export default SharedChargeFields
